import { useEffect, useMemo } from 'react'

import {
  type Control,
  Controller,
  type FieldValues,
  type UseFormSetValue,
  useFieldArray,
  useFormState,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  FieldRoot,
  HStack,
  IconButton,
  Input,
  Stack,
  TertiaryButton,
  Text,
  Tooltip,
  VStack,
} from '@enechain/ecloud-designsystem'
import { FaPlusRegular, FaXmarkRegular } from '@enechain/ecloud-icons'

import { FieldErrorText } from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import { type CivilDate, todayCivilDate } from '~/models/civilDate'
import { isActiveOn } from '~/models/validPeriod'
import {
  type BgMasterRow,
  convertListBgMastersResponse,
  useListBgMasters,
} from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import type { SubmitterMasterFormInput } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/models/schema'
import { PeriodFields } from '~/pages/AdminOcctoMaster/tabs/_common/components/PeriodFields'
import { SubFormLabel } from '~/pages/AdminOcctoMaster/tabs/_common/components/SubFormLabel'
import { useTodayJst } from '~/pages/AdminOcctoMaster/tabs/_common/hooks/useTodayJst'
import {
  resolveAsOfDate,
  resolveExpiryFallbackLabel,
} from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

type Props = {
  control: Control<SubmitterMasterFormInput>
  setValue: UseFormSetValue<SubmitterMasterFormInput>
}

/**
 * 所属 BG 動的リストフィールド。
 * useFieldArray で行を追加/削除する。
 * BG 選択は useListBgMasters で取得した一覧を select で表示する。
 *
 * 4710-plan-submitter-bg-validation 設計書 §4:
 * - 候補 select は親 form の licenseType / areaCode で filter する
 *   (AC-OMS-005b、事業者不問 = 代理提出 BG を含む)
 * - 親 form の licenseType / areaCode 変更で候補外になった member は
 *   自動的に除去する (AC-04)
 * - 選択時に bgName に加え bgLicenseType / bgAreaCode も denormalize
 *   して setValue する (schema.ts の superRefine defensive check が使う)
 *
 * design (`design/admin/occto.html` #sm-bg-members 内 `.occto-member-row--smbg`)
 * のレイアウトに寄せる (2026-08-05):
 * - Section header: `.occto-sub-header` (border-top + label + Add ボタン右端)
 * - 各行: カード化 (Surface.secondary + padding + borderRadius)
 * - 1 段目 = BGコード select + BG名称 (auto-fill, disabled input) + X 削除
 * - 2 段目 = 適用開始日 + 適用終了日 (共通 `PeriodFields` = `CompactCivilDateField`)
 * - Empty state = 「所属BGが未登録です」
 */
export const SubmitterBgMemberFields = ({
  control,
  setValue,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { data: bgMastersData } = useListBgMasters()
  const bgMasters: BgMasterRow[] = useMemo(
    () =>
      bgMastersData !== undefined
        ? convertListBgMastersResponse(bgMastersData)
        : [],
    [bgMastersData],
  )

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'members',
  })

  const watchedLicenseType = useWatch({ control, name: 'licenseType' })
  const watchedAreaCode = useWatch({ control, name: 'areaCode' })
  const watchedMembers = useWatch({ control, name: 'members' })
  const watchedRecordValidFrom = useWatch({ control, name: 'validFrom' })
  const today = useTodayJst()
  // BG 選択は FormSelect (= 内部に Controller を持つ) に委ねるため、 行ごとの
  // エラーは Controller の fieldState ではなく form state から引く。
  const { errors } = useFormState({ control, name: 'members' })
  const memberError = (index: number): string | undefined =>
    errors.members?.[index]?.bgMasterId?.message

  // AC-OMS-005b: 候補は licenseType × areaCode が一致する BgMaster のみ
  // (事業者は問わない = 代理提出 BG を候補に含める)。 as-of narrow は含まない
  // (= 削除判定専用の集合。 occto-master-fk-as-of-filter 設計書 Decision 5 で
  // option 用集合と分離)。
  const candidateBgMasters = useMemo(
    () =>
      bgMasters.filter(
        (bg) =>
          bg.licenseType === watchedLicenseType &&
          bg.areaCode === watchedAreaCode,
      ),
    [bgMasters, watchedLicenseType, watchedAreaCode],
  )

  // AC-MOI-023 (occto-master-fk-as-of-filter 設計書 Decision 5): option (menu)
  // 用の候補は License×Area × 行の対象日。 対象日で候補外になった既存 member は
  // 自動削除せず fallback (「（適用前）」「（終了）」) で残す (= candidateBgMasters
  // による削除判定とは別の集合)。 表示文字列は design HTML `_smRenderBgMembers` と
  // 同じ「<BGコード> - <BG名称>」 形式。
  const candidateBgOptionsOn = (
    asOf: CivilDate,
  ): Array<{ value: string; label: string }> =>
    candidateBgMasters
      .filter((bg) => isActiveOn(bg.validFrom, bg.validTo, asOf))
      .map((bg) => ({
        value: bg.id,
        label: `${bg.bgCode} - ${bg.bgName}`,
      }))

  // 候補外 (as-of narrow 後) の既存参照 fallback ラベル。 対象日時点で終了して
  // いれば「（終了）」サフィックス付き、 物理削除済みなら「(削除済み)」を返す。
  // list クエリ取得中は unfiltered に undefined を渡し、 実在参照が一瞬
  // 「(削除済み)」に誤表示されるのを防ぐ (PR #5920 レビュー指摘)。
  const bgFallbackLabel = (id: string, asOf: CivilDate): string | undefined =>
    resolveExpiryFallbackLabel(
      bgMastersData === undefined ? undefined : bgMasters,
      id,
      asOf,
      {
        idOf: (bg) => bg.id,
        labelOf: (bg) => `${bg.bgCode} - ${bg.bgName}`,
        validTo: (bg) => bg.validTo,
        deletedLabel: t('page:adminOcctoMaster.common.masterFallback.deleted'),
        expiredSuffix: t(
          'page:adminOcctoMaster.common.masterFallback.expiredSuffix',
        ),
        future: {
          validFrom: (bg) => bg.validFrom,
          suffix: t('page:adminOcctoMaster.common.masterFallback.futureSuffix'),
        },
      },
    )

  // AC-04: licenseType / areaCode の変更で候補外になった member を自動的に
  // form state から除去する。bgMasters が未取得 (= fetch 未完了) の間は
  // fail-open で何もしない (取得前の一時的な候補 0 件で誤って全 member を
  // 消さないため)。
  useEffect(() => {
    if (bgMasters.length === 0) return
    const members = watchedMembers ?? []
    if (members.length === 0) return
    const candidateIds = new Set(candidateBgMasters.map((bg) => bg.id))
    const indicesToRemove = members
      .map((member, index) => ({ index, bgMasterId: member.bgMasterId }))
      .filter(
        ({ bgMasterId }) => bgMasterId !== '' && !candidateIds.has(bgMasterId),
      )
      .map(({ index }) => index)
    if (indicesToRemove.length > 0) {
      remove(indicesToRemove)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedLicenseType, watchedAreaCode])

  return (
    <Stack gap={1}>
      {/* Section header (design `.occto-sub-header`):
          - border-top で他セクションと区切り
          - 左 = 「所属BG」見出し (textStyle="modalSectionTitle" = lg/700)
          - 右 = 「追加」button (TertiaryButton size sm + FaPlusRegular)
          追加ボタンは design ではセクションヘッダの右端にあり、 従来 impl の
          「行リストの下」から header に移動する。 */}
      <HStack
        align="center"
        justify="space-between"
        marginTop={4}
        marginBottom={2}
        paddingTop={3}
        borderTopWidth="1px"
        borderTopColor="Border.lowEmphasis"
      >
        <Text textStyle="modalSectionTitle" color="Text.highEmphasis">
          {t('page:adminOcctoMaster.submitterMaster.form.members')}
        </Text>
        <TertiaryButton
          onClick={() => {
            append({
              bgMasterId: '',
              bgName: '',
              validFrom: todayCivilDate(),
              validTo: undefined,
            })
          }}
          size="sm"
        >
          <FaPlusRegular />
          {t('page:adminOcctoMaster.submitterMaster.form.addMember')}
        </TertiaryButton>
      </HStack>
      <VStack align="stretch" gap={2}>
        {fields.length === 0 && (
          <Text color="Text.midEmphasis" fontSize="md" paddingY={2}>
            {t('page:adminOcctoMaster.submitterMaster.form.memberEmpty')}
          </Text>
        )}
        {fields.map((field, index) => {
          const rowAsOf = resolveAsOfDate(
            watchedMembers?.[index]?.validFrom,
            watchedRecordValidFrom,
            today,
          )
          return (
            <Box
              key={field.id}
              // 構成事業者カードと同じ扱いに揃える (2026-08-26 デザイナー指摘):
              // padding 16px + 行の操作 (削除) をカード右上へ絶対配置。
              position="relative"
              padding={4}
              borderRadius="base"
              backgroundColor="Surface.secondary"
            >
              <Box position="absolute" zIndex={1} top={4} right={4}>
                <Tooltip
                  content={t(
                    'page:adminOcctoMaster.submitterMaster.form.memberRemove',
                  )}
                >
                  <IconButton
                    color="Text.negativeMid"
                    _hover={{ color: 'Text.negativeHigh' }}
                    aria-label={t(
                      'page:adminOcctoMaster.submitterMaster.form.memberRemove',
                    )}
                    onClick={() => {
                      remove(index)
                    }}
                    size="sm"
                    variant="ghost"
                  >
                    <FaXmarkRegular />
                  </IconButton>
                </Tooltip>
              </Box>
              <Stack gap={2}>
                {/* 1 段目: BGコード select + BG名称 (auto-fill 表示)。
                  削除は右上へ移したので、 その 32px ぶんを空ける。 */}
                <HStack align="flex-end" gap={2} paddingRight="32px">
                  {/* BG 選択 (AC-OMS-005b: licenseType × areaCode 一致の候補のみ)。
                    単一 select の正は DS `Select`
                    (`~/components/common/FormSelect` の JSDoc 参照)。 */}
                  <FieldRoot
                    alignItems="stretch"
                    flex={1}
                    invalid={memberError(index) !== undefined}
                  >
                    <SubFormLabel>
                      {t('page:adminOcctoMaster.submitterMaster.form.bgCode')}
                    </SubFormLabel>
                    <FormSelect
                      aria-label={t(
                        'page:adminOcctoMaster.submitterMaster.form.members',
                      )}
                      control={control}
                      fallbackLabel={(id) => bgFallbackLabel(id, rowAsOf)}
                      name={`members.${index}.bgMasterId`}
                      onValueChange={(selectedId) => {
                        // bgName / bgLicenseType / bgAreaCode を bgMasterId と
                        // 同時に更新する (setValue 経由で RHF に伝播。 license/area は
                        // schema.ts superRefine の defensive check が使う
                        // denormalized field)。
                        const selectedBg = bgMasters.find(
                          (bg) => bg.id === selectedId,
                        )
                        if (selectedBg !== undefined) {
                          setValue(`members.${index}.bgName`, selectedBg.bgName)
                          setValue(
                            `members.${index}.bgLicenseType`,
                            selectedBg.licenseType,
                          )
                          setValue(
                            `members.${index}.bgAreaCode`,
                            selectedBg.areaCode,
                          )
                        } else {
                          // 未選択に戻した時は denormalized field もクリア
                          setValue(`members.${index}.bgName`, '')
                        }
                      }}
                      options={candidateBgOptionsOn(rowAsOf)}
                      placeholder={t(
                        'page:adminOcctoMaster.submitterMaster.form.bgMasterUnselected',
                      )}
                    />
                    {memberError(index) !== undefined && (
                      <FieldErrorText>{memberError(index)}</FieldErrorText>
                    )}
                  </FieldRoot>
                  {/* BG名称 (auto-fill, disabled) — design 準拠。 bgMasterId の
                    select onChange で `members.${index}.bgName` が setValue
                    されるので、 それを readonly 表示する。 */}
                  <Controller
                    control={control}
                    name={`members.${index}.bgName`}
                    render={({ field: bgNameField }) => (
                      <FieldRoot alignItems="stretch" flex={1}>
                        <SubFormLabel>
                          {t(
                            'page:adminOcctoMaster.submitterMaster.form.bgName',
                          )}
                        </SubFormLabel>
                        <Input
                          aria-label={t(
                            'page:adminOcctoMaster.submitterMaster.form.bgName',
                          )}
                          disabled
                          value={bgNameField.value ?? ''}
                        />
                      </FieldRoot>
                    )}
                  />
                </HStack>
                {/* 2 段目: 適用開始日 + 適用終了日 (共通 PeriodFields) */}
                <PeriodFields
                  basePath={`members.${index}`}
                  clearAsNull
                  control={control as unknown as Control<FieldValues>}
                  i18nNs="adminOcctoMaster.submitterMaster"
                  spacerWidth="0px"
                />
              </Stack>
            </Box>
          )
        })}
      </VStack>
    </Stack>
  )
}
